import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login, validateTotp } from '../../services/authService';
import { Card, Button, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { byPrefixAndName } from '@awesome.me/kit-8685c875a9/icons';
import QRCode from "react-qr-code";
import '../../scss/components/login.scss';

const LoginForm = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [show2faSetup, setShow2faSetup] = useState(false);
    const [show2fa, setShow2fa] = useState(false);
    const [twoFactorCode, setTwoFactorCode] = useState(null);
    const [twoFactorError, SetTwoFactorError] = useState('');
    const [disable2fa, setDisable2fa] = useState(false);
    const [disableLogin, setDisableLogin] = useState(false);
    const [totpUrl, setTotpUrl] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setLoading(true);

        try {
            await login(email, password, rememberMe);
            setSuccess('Login successful');
            setError(null);
            navigate('/dashboard');
        } catch (err) {
            if (err instanceof Error && err.message === "2FA_SETUP") {
                // first-time 2FA setup flow
                setTotpUrl(err.totpUrl)
                setShow2faSetup(true)
            } else if (err instanceof Error && err.message === "2FA") {
                // 2FA code prompt
                setShow2fa(true)
            } else if (err instanceof Error && err.message === "LOGIN_FAIL") {
                setError('Invalid email or password');
            } else if (err instanceof Error && err.message === "LOCKED_OUT") {
                setError('Your account has been locked out due to continued failed attempts, please try again later.');
            } else {
                setError('Something went wrong, please try again later');
            }
            
            setSuccess(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Row className="justify-content-center align-items-center vh-100 m-0">
            <Col xs={12} md={6} lg={5} xl={4}>
                <Row>
                    <Col>
                        <img className="d-block mx-auto img-fluid mb-4" width="75px" src="/bestU.png" alt="Logo" />
                    </Col>
                </Row>
            {!show2faSetup && !show2fa && (
                <Card className="p-4 shadow-lg border-0 rounded-4">
                    <Card.Body>
                        <div className="text-center mb-4">
                            <FontAwesomeIcon icon={byPrefixAndName.fas['lock']} />
                            <h3 className="mt-2">Sign In</h3>
                        </div>

                        <Form onSubmit={handleSubmit}>
                            <Form.Group className="mb-3">
                                <Form.Label>Email address</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="Enter email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Password</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Enter password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        <FontAwesomeIcon icon={byPrefixAndName.fas[showPassword ? 'eye-slash' : 'eye']} />
                                    </Button>
                                </InputGroup>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Remember Me"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                />
                            </Form.Group>

                            {error && <p className="text-danger text-center">{error}</p>}
                            {success && <p className="text-success text-center">{success}</p>}

                            <div className="text-center mb-3">

                                <Button type="submit" variant="primary" disabled={loading}>
                                    {loading ? (
                                        <>
                                            Signing in...&nbsp;
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        </>
                                    ) : (
                                        <>
                                            Sign In <FontAwesomeIcon icon={byPrefixAndName.fas['right-to-bracket']} />
                                        </>
                                    )}
                                </Button>
                            </div>

                            <div className="text-center">
                                <Button type="submit" variant="" className="p-0">
                                    <Link to="/forgotPassword">
                                        Forgot Password
                                    </Link>
                                </Button>
                            </div>


                        </Form>
                    </Card.Body>
                    </Card>
                )}
                {show2faSetup && (
                    <Card className="p-4 shadow-lg border-0 rounded-4">
                        <Card.Body>
                            <div className="text-center mb-4">
                                <FontAwesomeIcon icon={byPrefixAndName.fas['lock']} />
                                <h3 className="mt-2">Two Factor Authentication</h3>
                                <p>
                                    To get setup, please scan the barcode below using your Authenticator app on your <b>work phone</b>, then choose continue.
                                </p>
                            </div>
                            <div className="mb-5" style={{ height: "auto", margin: "0 auto", maxWidth: 150, width: "100%" }}>
                                <QRCode
                                    size={256}
                                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                    value={totpUrl}
                                    viewBox={`0 0 256 256`}
                                />
                            </div>
                            <div className="text-center mb-3">
                                <Button type="submit" variant="primary" onClick={() => {
                                    setTotpUrl('')
                                    setShow2faSetup(false)
                                    setShow2fa(true)
                                } }>
                                    Continue <FontAwesomeIcon icon={byPrefixAndName.fas['right-to-bracket']} />
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                )}

                {show2fa && (
                    <Card className="p-4 shadow-lg border-0 rounded-4">
                        <Card.Body>
                            <div className="text-center mb-4">
                                <FontAwesomeIcon icon={byPrefixAndName.fas['lock']} />
                                <h3 className="mt-2">Two Factor Authentication</h3>
                                <p>
                                    Please enter your 6 digit code from your authenticator app
                                </p>
                            </div>

                            <Form onSubmit={async (e) => {
                                e.preventDefault();
                                try {

                                    await validateTotp(twoFactorCode, email);

                                    setSuccess('Login successful');
                                    setError(null);
                                    navigate('/dashboard');

                                } catch (err) {
                                    if (err instanceof Error && err.message === "FAILED_TOTP") {
                                        SetTwoFactorError("You provided an incorrect code, please try again")
                                        setDisable2fa(false)
                                    } else if (err instanceof Error && err.message === "LOCKED_OUT") {
                                        SetTwoFactorError("Your account has been locked out due to continued failed attempts, please try again later.")
                                        setDisable2fa(true)
                                    } else {
                                        SetTwoFactorError('Something went wrong, please try again later');
                                        setDisable2fa(false)
                                    }
                                }
                            }}>
                            <div className="text-center mb-4">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="\d{6}"
                                    maxLength={6}
                                    placeholder="Enter 6-digit code"
                                    className="form-control text-center fs-4"
                                    value={twoFactorCode}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '');
                                       setTwoFactorCode(val);
                                    }}
                                />
                            </div>

                                <div className="text-center mb-3">
                                    <Button type="submit" variant="primary" disabled={disable2fa}>
                                    Submit
                                </Button>
                                </div>
                                {twoFactorError && <p className="text-danger text-center">{twoFactorError}</p>}
                            </Form>
                        </Card.Body>
                    </Card>
                )}


            </Col>
        </Row>
    );
};

export default LoginForm;
